import { useEffect, useState, useRef } from "react";
import api from "../api";
import CompanyCommsOverview from "./CompanyCommsOverview";
import { Modal } from "../components/ui";

export default function ConsentAdmin() {
  const [stats, setStats] = useState(null);
  const [camp, setCamp] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {subject, html}
  const poll = useRef(null);

  const openPreview = async () => {
    try { const r = await api.get("/optin/preview"); setPreview(r.data); }
    catch (e) { setMsg("Errore anteprima: " + (e.response?.data?.detail || e.message)); }
  };

  const loadStats = () => api.get("/optin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadCamp = () => api.get("/optin/campaign/status").then((r) => setCamp(r.data)).catch(() => {});
  const loadContacts = () => api.get(`/optin/contacts${filter ? `?status=${filter}` : ""}`).then((r) => setContacts(r.data)).catch(() => {});

  useEffect(() => { loadStats(); loadCamp(); }, []);
  useEffect(() => { loadContacts(); }, [filter]);
  useEffect(() => {
    if (camp?.status === "running" && !poll.current) {
      poll.current = setInterval(() => { loadCamp(); loadStats(); }, 3000);
    }
    if (camp?.status !== "running" && poll.current) { clearInterval(poll.current); poll.current = null; loadContacts(); }
    return () => { if (poll.current) { clearInterval(poll.current); poll.current = null; } };
  }, [camp?.status]);

  const importLeads = async () => {
    setBusy(true); setMsg("Importo l'anagrafica…");
    try { const r = await api.post("/optin/import-leads"); setMsg(`Importati ${r.data.imported} contatti (saltati ${r.data.skipped}).`); loadStats(); loadContacts(); }
    catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const startCampaign = async (resend = false) => {
    const q = resend
      ? "Re-inviare la richiesta di consenso a TUTTI i contatti in attesa che non hanno ancora risposto (anche chi l'ha già ricevuta)? Gli invii sono scaglionati."
      : "Inviare la richiesta di consenso ai contatti che non l'hanno mai ricevuta? Gli invii sono scaglionati.";
    if (!window.confirm(q)) return;
    setBusy(true); setMsg("");
    try { const r = await api.post("/optin/campaign/repermission", { resend }); setMsg(`Campagna avviata: ${r.data.to_send} email in coda.`); loadCamp(); }
    catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const S = stats || {};
  const running = camp?.status === "running";
  const pct = camp?.total ? Math.round(((camp.sent || 0) + (camp.failed || 0)) / camp.total * 100) : 0;

  return (
    <div>
      <div className="panel">
        <h3>Consensi privacy (GDPR)</h3>
        <p className="muted">Double opt-in: i contatti diventano "confermati" solo cliccando il link nell'email. Solo i confermati possono entrare nel funnel.</p>
        <p className="muted" style={{ fontSize: 12.5 }} data-testid="consent-automations-note">🛡️ <b>Automatismi attivi</b>: richiesta inviata da sola a ogni nuovo cliente/lead (persone e aziende) · recupero giornaliero di chi manca all'appello · promemoria automatico dopo 30 giorni a chi non ha risposto (una sola volta).</p>
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <button className="btn sm" onClick={openPreview} data-testid="optin-preview-btn">👁 Anteprima email di consenso</button>
          <span className="muted" style={{ fontSize: 12 }}>È la email inviata automaticamente ai nuovi lead/clienti e usata dalla campagna re-permission.</span>
        </div>
        <div className="tag-filter" style={{ marginBottom: 12 }} data-testid="consent-stats">
          <span className="chip" style={{ background: "#16a34a" }}>Confermati: {S.confirmed ?? "—"}</span>
          <span className="chip" style={{ background: "#d97706" }}>In attesa: {S.pending ?? "—"}</span>
          <span className="chip" style={{ background: "#64748b" }}>Disiscritti: {S.unsubscribed ?? "—"}</span>
          <span className="chip" style={{ background: "#dc2626" }}>Suppression: {S.suppression ?? "—"}</span>
        </div>
        <div className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={importLeads} disabled={busy || running} data-testid="import-leads-btn">1. Importa clienti e lead dall'anagrafica</button>
          <button className="btn primary" onClick={() => startCampaign(false)} disabled={busy || running || !(S.repermission_pending > 0)} data-testid="start-campaign-btn">2. Invia richiesta ai nuovi ({S.repermission_pending ?? 0})</button>
          <button className="btn" onClick={() => startCampaign(true)} disabled={busy || running || !((S.repermission_pending || 0) + (S.repermission_resend || 0) > 0)} data-testid="resend-campaign-btn">↻ Re-invia a chi non ha risposto ({(S.repermission_pending || 0) + (S.repermission_resend || 0)})</button>
        </div>
        {msg && <p className="muted" data-testid="consent-msg" style={{ marginTop: 8 }}>{msg}</p>}
      </div>

      {camp && camp.status && camp.status !== "idle" && (
        <div className="panel" style={{ marginTop: 16 }} data-testid="campaign-progress">
          <div className="toolbar"><h3 style={{ margin: 0 }}>Campagna re-permission</h3><div className="spacer" />
            <span className="pill">{running ? "in corso" : camp.status}</span>
          </div>
          <div style={{ background: "rgba(255,255,255,.08)", borderRadius: 8, height: 14, overflow: "hidden", margin: "10px 0" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg,#0ea5e9,#22c55e)", transition: "width .3s" }} />
          </div>
          <p className="muted">Inviate {camp.sent || 0} / {camp.total || 0} · errori {camp.failed || 0}</p>
        </div>
      )}

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="toolbar">
          <h3 style={{ margin: 0 }}>Contatti</h3>
          <div className="spacer" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="contact-filter">
            <option value="">Tutti</option>
            <option value="confirmed">Confermati</option>
            <option value="pending">In attesa</option>
            <option value="unsubscribed">Disiscritti</option>
          </select>
        </div>
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>Email</th><th>Nome</th><th>Stato</th><th>Origine</th></tr></thead>
          <tbody>
            {contacts.slice(0, 500).map((c) => (
              <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                <td>{c.email}</td><td>{c.name || "—"}</td>
                <td><span className="pill">{c.status}</span></td>
                <td className="muted">{c.source}</td>
              </tr>
            ))}
            {contacts.length === 0 && <tr><td colSpan={4}><div className="empty">Nessun contatto.</div></td></tr>}
          </tbody>
        </table>
        {contacts.length > 500 && <p className="muted">Mostrati i primi 500 di {contacts.length}.</p>}
      </div>

      <CompanyCommsOverview />

      {preview && (
        <Modal title={`Anteprima — ${preview.subject}`} onClose={() => setPreview(null)} wide>
          <iframe title="anteprima-consenso" sandbox="" srcDoc={preview.html} data-testid="optin-preview-iframe" style={{ width: "100%", height: "70vh", border: "1px solid #22222e", borderRadius: 10, background: "#0d0d14" }} />
          <div className="modal-actions"><button className="btn" onClick={() => setPreview(null)}>Chiudi</button></div>
        </Modal>
      )}
    </div>
  );
}
